import { parseState, updateUrl, serializeState, type AppState, type WritingStage, markAsRead, unmarkAsRead, isRead, toggleStarred, isStarred, getStarredItems } from "./state.js";
import { marked, Renderer } from "marked";
import { tr } from "./translations.js";
import { initSavedPage, initSavedHeaderLink } from "./saved.js";

// Configure marked to open external links in new tabs
const renderer = new Renderer();
renderer.link = function({ href, title, text }: { href: string; title?: string | null; text: string }) {
  const titleAttr = title ? ` title="${title}"` : "";
  if (href && (href.startsWith('http://') || href.startsWith('https://'))) {
    return `<a href="${href}"${titleAttr} target="_blank" rel="noopener noreferrer">${text}</a>`;
  }
  return `<a href="${href}"${titleAttr}>${text}</a>`;
};
marked.use({ renderer });

// Global state
let state: AppState;
let debugMode = false;
let showResearchMode = false;

function initDebugMode(): void {
  debugMode = localStorage.getItem("debugMode") === "true";
  showResearchMode = localStorage.getItem("showResearchMode") === "true";
  updateDebugModeUI();
  updateResearchModeUI();

  const toggle = document.getElementById("debug-toggle");
  if (toggle) {
    toggle.addEventListener("click", () => {
      debugMode = !debugMode;
      localStorage.setItem("debugMode", debugMode.toString());
      updateDebugModeUI();
      // Re-render if on guide page
      if (window.location.pathname.includes("guide")) {
        initGuidePage();
      }
    });
  }

  const researchToggle = document.getElementById("research-toggle");
  if (researchToggle) {
    researchToggle.addEventListener("click", () => {
      showResearchMode = !showResearchMode;
      localStorage.setItem("showResearchMode", showResearchMode.toString());
      updateResearchModeUI();
      // Re-render if on guide page
      if (window.location.pathname.includes("guide")) {
        initGuidePage();
      }
    });
  }
}

function updateDebugModeUI(): void {
  document.body.classList.toggle("debug-mode", debugMode);
  const toggle = document.getElementById("debug-toggle");
  if (toggle) {
    toggle.textContent = debugMode ? tr().debugActive : tr().debug;
    toggle.classList.toggle("text-blue-600", debugMode);
    toggle.classList.toggle("border-blue-300", debugMode);
  }
}

function updateResearchModeUI(): void {
  document.body.classList.toggle("show-research-mode", showResearchMode);
  const toggle = document.getElementById("research-toggle");
  if (toggle) {
    toggle.textContent = showResearchMode ? tr().showResearchActive : tr().showResearch;
    toggle.classList.toggle("text-blue-600", showResearchMode);
    toggle.classList.toggle("border-blue-300", showResearchMode);
  }
}

// Add copy buttons to all pre blocks in the document
function addCopyButtons(container?: Element): void {
  const root = container || document;
  const preBlocks = root.querySelectorAll("pre");

  preBlocks.forEach((pre) => {
    // Skip if already has a copy button
    if (pre.querySelector(".copy-btn")) return;

    // Make sure pre is positioned relative for absolute button positioning
    pre.style.position = "relative";

    const copyBtn = document.createElement("button");
    copyBtn.className = "copy-btn";
    copyBtn.textContent = "Copy";
    copyBtn.type = "button";

    copyBtn.addEventListener("click", async () => {
      const code = pre.querySelector("code");
      const text = code ? code.textContent : pre.textContent;

      try {
        await navigator.clipboard.writeText(text || "");
        copyBtn.textContent = "Copied!";
        copyBtn.classList.add("copied");

        setTimeout(() => {
          copyBtn.textContent = "Copy";
          copyBtn.classList.remove("copied");
        }, 2000);
      } catch (err) {
        console.error("Failed to copy:", err);
        copyBtn.textContent = "Failed";
        setTimeout(() => {
          copyBtn.textContent = "Copy";
        }, 2000);
      }
    });

    pre.appendChild(copyBtn);
  });
}

function init(): void {
  state = parseState();

  // Initialize debug mode
  initDebugMode();

  // Initialize writing stage nav on all pages (if present)
  initWritingStageNav();

  // Initialize saved link in header on all pages
  initSavedHeaderLink();

  // Initialize based on current page
  const path = window.location.pathname;

  if (path.includes("guide")) {
    initGuidePage();
  } else if (path.includes("saved")) {
    initSavedPage();
  }

}

function initTopicsPage(): void {
  const form = document.getElementById("topics-form");
  if (!form) return;

  // Set initial checkbox states
  state.topics.forEach((id) => {
    const checkbox = document.getElementById(id) as HTMLInputElement;
    if (checkbox) checkbox.checked = true;
  });

  // Listen for changes
  form.addEventListener("change", () => {
    const checkboxes = form.querySelectorAll<HTMLInputElement>(
      'input[name="topics"]:checked'
    );
    state.topics = Array.from(checkboxes).map((cb) => cb.value);
    updateUrl(state);
  });
}

// Initialize audience form - used on both audience page and overview page
function initAudienceForm(onChangeCallback?: () => void): void {
  const form = document.getElementById("audience-form");
  if (!form) return;

  // Set initial states
  if (state.audience.closeness) {
    const radio = document.getElementById(state.audience.closeness) as HTMLInputElement;
    if (radio) radio.checked = true;
  }
  if (state.audience.agreement) {
    const radio = document.getElementById(state.audience.agreement) as HTMLInputElement;
    if (radio) radio.checked = true;
  }
  // Context is now a 3-way radio: general (neither), neighbors, or faith
  if (state.audience.neighbors) {
    const radio = document.getElementById("context-neighbors") as HTMLInputElement;
    if (radio) radio.checked = true;
  } else if (state.audience.faith) {
    const radio = document.getElementById("context-faith") as HTMLInputElement;
    if (radio) radio.checked = true;
  } else {
    // Default to general if neither is set
    const radio = document.getElementById("context-general") as HTMLInputElement;
    if (radio) radio.checked = true;
  }

  // Listen for changes
  form.addEventListener("change", () => {
    const closenessRadio = form.querySelector<HTMLInputElement>(
      'input[name="closeness"]:checked'
    );
    const agreementRadio = form.querySelector<HTMLInputElement>(
      'input[name="agreement"]:checked'
    );
    const contextRadio = form.querySelector<HTMLInputElement>(
      'input[name="context"]:checked'
    );

    state.audience.closeness = closenessRadio?.value as AppState["audience"]["closeness"];
    state.audience.agreement = agreementRadio?.value as AppState["audience"]["agreement"];
    // Context radio: general clears both, neighbors sets neighbors, faith sets faith
    const contextValue = contextRadio?.value;
    state.audience.neighbors = contextValue === "neighbors" ? true : undefined;
    state.audience.faith = contextValue === "faith" ? true : undefined;

    updateUrl(state);

    // Call optional callback (e.g., to re-render guidance list)
    if (onChangeCallback) onChangeCallback();
  });
}

function initAudiencePage(): void {
  initAudienceForm();

  // Validate required fields before navigation
  const nextButton = document.querySelector('a[href*="blockers"]');
  if (nextButton) {
    nextButton.addEventListener("click", (e) => {
      if (!state.audience.closeness || !state.audience.agreement) {
        e.preventDefault();
        e.stopPropagation();

        // Show validation message
        let errorEl = document.getElementById("audience-error");
        const audienceForm = document.getElementById("audience-form");
        if (!errorEl && audienceForm) {
          errorEl = document.createElement("div");
          errorEl.id = "audience-error";
          errorEl.className = "bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4";
          audienceForm.parentElement?.insertBefore(errorEl, audienceForm);
        }

        const missing: string[] = [];
        if (!state.audience.closeness) missing.push("how close they are to you");
        if (!state.audience.agreement) missing.push("where they stand");

        if (errorEl) {
          errorEl.textContent = `Please select ${missing.join(" and ")} before continuing.`;
          errorEl.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }
    });
  }
}

function initBlockersPage(): void {
  const form = document.getElementById("blockers-form");
  if (!form) return;

  // Set initial checkbox states
  state.blockers.forEach((id) => {
    const checkbox = document.getElementById(id) as HTMLInputElement;
    if (checkbox) checkbox.checked = true;
  });

  // Listen for changes
  form.addEventListener("change", () => {
    const checkboxes = form.querySelectorAll<HTMLInputElement>(
      'input[name="blockers"]:checked'
    );
    state.blockers = Array.from(checkboxes).map((cb) => cb.value);
    updateUrl(state);
  });
}

function initGuidePage(): void {
  const listEl = document.getElementById("guidance-list");
  const emptyEl = document.getElementById("empty-state");
  const cardsDataEl = document.getElementById("cards-data");

  if (!listEl || !cardsDataEl) return;

  const cards: AllCards = JSON.parse(cardsDataEl.textContent || "{}");

  // Helper to re-render the guidance list
  const rerender = () => renderGuidanceList(listEl, emptyEl, cards);

  // Render the list
  rerender();

  // Initialize audience form with callback to re-render on change and update summary
  initAudienceForm(() => {
    rerender();
    updateAudienceSummary();
  });

  // Set up collapsible audience section
  initAudienceCollapsible();

  // Re-render when writing stage changes
  const nav = document.getElementById("writing-stage-nav");
  if (nav) {
    nav.addEventListener("click", () => {
      // Small delay to let state update
      setTimeout(rerender, 10);
    });
  }
}

function updateAudienceSummary(): void {
  const summaryText = document.getElementById("audience-summary-text");
  if (!summaryText) return;

  const parts: string[] = [];

  // Closeness
  if (state.audience.closeness === "close") {
    parts.push(tr().audienceSummaryClose);
  } else if (state.audience.closeness === "extended") {
    parts.push(tr().audienceSummaryExtended);
  }

  // Agreement
  if (state.audience.agreement === "sympathetic") {
    parts.push(tr().audienceSummarySympathetic);
  } else if (state.audience.agreement === "some-disagree") {
    parts.push(tr().audienceSummarySomeDisagree);
  }

  // Context (3-way: general, neighbors, or faith)
  if (state.audience.neighbors) {
    parts.push(tr().audienceSummaryNeighbors);
  } else if (state.audience.faith) {
    parts.push(tr().audienceSummaryFaith);
  } else {
    parts.push(tr().audienceSummaryGeneral);
  }

  if (parts.length === 0) {
    summaryText.textContent = tr().audiencePrompt;
    summaryText.classList.remove("font-medium", "text-gray-900");
    summaryText.classList.add("text-gray-600");
  } else {
    summaryText.textContent = parts.join(" · ");
    summaryText.classList.add("font-medium", "text-gray-900");
    summaryText.classList.remove("text-gray-600");
  }
}

function initAudienceCollapsible(): void {
  const details = document.getElementById("audience-details") as HTMLDetailsElement | null;
  const doneBtn = document.getElementById("audience-done-btn");
  const summary = document.getElementById("audience-summary");

  if (!details || !summary) return;

  // Update summary on load
  updateAudienceSummary();

  // If audience is already selected, start collapsed
  const hasAudience = state.audience.closeness || state.audience.agreement || state.audience.neighbors || state.audience.faith;
  details.open = !hasAudience;

  // Manage open/close state manually to avoid webview quirks
  let isOpen = details.open;

  // Only allow toggle via summary click or done button
  summary.addEventListener("click", (e) => {
    e.preventDefault(); // Prevent default details toggle
    isOpen = !isOpen;
    details.open = isOpen;
  });

  // Done button closes
  if (doneBtn) {
    doneBtn.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      isOpen = false;
      details.open = false;
    });
  }

  // Block any other toggle attempts
  details.addEventListener("toggle", () => {
    if (details.open !== isOpen) {
      details.open = isOpen;
    }
  });
}

interface RenderedPrompt {
  text: string;
  matched?: boolean; // Has a match condition that was satisfied
  matchedCondition?: string;
  hidden?: boolean; // In debug mode, would be hidden
  hiddenReason?: string;
}

interface RenderedPromptsGroup {
  title?: string;
  prompts: string[]; // Simple version for non-debug
  debugPrompts?: RenderedPrompt[]; // Debug version with match info
  matched?: boolean; // Group has a match condition that was satisfied
  matchedCondition?: string;
  hidden?: boolean; // In debug mode, would be hidden
  hiddenReason?: string;
}

interface DebugGuidanceVariant {
  content: string;
  match?: string; // undefined means default
  isActive: boolean;
}

interface ResearchSource {
  ref: string;
  summary: string;
}

interface GuidanceItem {
  id: string;
  title: string;
  content: string; // markdown
  promptGroups?: RenderedPromptsGroup[]; // filtered prompts for this audience
  sources?: ResearchSource[]; // Research sources for this item
  // Debug mode fields
  hidden?: boolean; // Item would be hidden (doesn't match)
  hiddenReason?: string; // Why it's hidden (the match expression that failed)
  matched?: boolean; // Item matched a specific condition (not just default)
  matchedCondition?: string; // The condition that was matched
  debugGuidanceVariants?: DebugGuidanceVariant[]; // All guidance variants for debug display
}

interface GuidanceGroup {
  title: string;
  items: GuidanceItem[];
}

interface StrategyItem {
  id: string;
  label: string;
  content: string; // markdown
  promptGroups?: RenderedPromptsGroup[]; // strategies can have prompts too
  sources?: ResearchSource[]; // Research sources
  // Debug mode fields
  hidden?: boolean;
  hiddenReason?: string;
}

interface BlockerItem {
  id: string;
  title: string;
  content: string; // markdown (reassurance)
  strategies: StrategyItem[];
  sources?: ResearchSource[]; // Research sources
  // Debug mode fields
  hidden?: boolean;
  hiddenReason?: string;
}

interface TechniqueItem {
  id: string;
  title: string;
  content: string; // markdown
  examples?: TechniqueExample[];
  promptGroups?: RenderedPromptsGroup[];
  sources?: ResearchSource[]; // Research sources
  // Debug mode fields
  hidden?: boolean;
  hiddenReason?: string;
  matched?: boolean;
  matchedCondition?: string;
  debugGuidanceVariants?: DebugGuidanceVariant[];
}

// Convert list item ref to card id (e.g., "topics/witnessed-something.card" -> "topics_witnessed-something")
function refToCardId(ref: string): string {
  return ref.replace(/\.card$/, "").replace(/\//g, "_");
}

// Find a list card by type
function getListByType(cards: AllCards, listType: string): ListCard | undefined {
  return Object.values(cards.lists).find(list => list.listType === listType);
}

// Convert a ListItem to a GuidanceItem for topics
function listItemToTopicGuidance(
  item: ListItem,
  cards: AllCards,
  audienceProfile: AudienceProfile,
  isRelevantToStage: (appliesTo?: WritingStage[]) => boolean
): GuidanceItem | null {
  const cardId = refToCardId(item.ref);
  const topic = cards.topics[cardId];
  if (!topic) return null;

  // Check if topic matches audience/stage conditions
  const matchFailed = topic.match && matchAudience(topic.match, audienceProfile) < 0;
  const stageFailed = !isRelevantToStage(topic.appliesTo);

  if ((matchFailed || stageFailed) && !debugMode) {
    return null; // Topic doesn't match current profile
  }

  const { content: guidance, matched, matchedCondition, debugVariants } = selectGuidanceWithDebug(
    topic.guidance,
    topic.conditionalGuidance,
    audienceProfile
  );
  const promptGroups = selectPromptGroups(topic.promptGroups, audienceProfile);

  return {
    id: cardId,
    title: getTopicTitle(cardId, item.title),
    content: guidance,
    promptGroups: promptGroups.length > 0 ? promptGroups : undefined,
    sources: topic.sources,
    hidden: matchFailed || stageFailed,
    hiddenReason: matchFailed ? topic.match : stageFailed ? "stage" : undefined,
    matched,
    matchedCondition,
    debugGuidanceVariants: debugVariants,
  };
}

// Convert a ListItem to a BlockerItem with strategies (with stage filtering)
function listItemToBlockerItem(
  item: ListItem,
  cards: AllCards,
  isRelevantToStage: (appliesTo?: WritingStage[]) => boolean,
  audienceProfile: AudienceProfile
): BlockerItem | null {
  const cardId = refToCardId(item.ref);
  const blocker = cards.blockers[cardId];
  if (!blocker) return null;

  const blockerHidden = !isRelevantToStage(blocker.appliesTo);

  // In non-debug mode, skip hidden blockers
  if (blockerHidden && !debugMode) return null;

  // Build strategy items
  const strategies: StrategyItem[] = [];
  if (blocker.strategies) {
    for (const stratRef of blocker.strategies) {
      const stratId = stratRef.ref
        .replace("blockers/strategies/", "blockers_strategies_")
        .replace(".card", "");
      const strategy = cards.strategies[stratId];
      if (!strategy) continue;

      const strategyHidden = !isRelevantToStage(strategy.appliesTo);

      // In non-debug mode, skip hidden strategies
      if (strategyHidden && !debugMode) continue;

      const promptGroups = selectPromptGroups(strategy.promptGroups, audienceProfile);
      strategies.push({
        id: stratId,
        label: stratRef.label,
        content: strategy.guidance,
        promptGroups: promptGroups.length > 0 ? promptGroups : undefined,
        sources: strategy.sources,
        hidden: strategyHidden,
        hiddenReason: strategyHidden ? "stage" : undefined,
      });
    }
  }

  return {
    id: cardId,
    title: item.title,
    content: blocker.reassurance,
    strategies,
    sources: blocker.sources,
    hidden: blockerHidden,
    hiddenReason: blockerHidden ? "stage" : undefined,
  };
}

// Convert a ListItem to a TechniqueItem
function listItemToTechniqueItem(
  item: ListItem,
  cards: AllCards,
  audienceProfile: AudienceProfile
): TechniqueItem | null {
  const cardId = refToCardId(item.ref);
  const technique = cards.techniques[cardId];
  if (!technique) return null;

  // Check if technique matches audience conditions
  const matchFailed = technique.match ? matchAudience(technique.match, audienceProfile) < 0 : false;

  if (matchFailed && !debugMode) {
    return null; // Technique doesn't match current profile
  }

  const { content: guidance, matched, matchedCondition, debugVariants } = selectGuidanceWithDebug(
    technique.guidance,
    technique.conditionalGuidance,
    audienceProfile
  );
  const promptGroups = selectPromptGroups(technique.promptGroups, audienceProfile);

  return {
    id: cardId,
    title: item.title,
    content: guidance,
    examples: technique.examples,
    promptGroups: promptGroups.length > 0 ? promptGroups : undefined,
    sources: technique.sources,
    hidden: matchFailed,
    hiddenReason: matchFailed ? technique.match : undefined,
    matched,
    matchedCondition,
    debugGuidanceVariants: debugVariants,
  };
}

function renderGuidanceList(
  listEl: HTMLElement,
  emptyEl: HTMLElement | null,
  cards: AllCards
): void {
  // Save which details elements are open before re-rendering
  const openSections = new Set<string>();
  listEl.querySelectorAll("details[open]").forEach((el) => {
    const section = el.getAttribute("data-section");
    const itemId = el.getAttribute("data-item-id");
    if (section) openSections.add(`section:${section}`);
    if (itemId) openSections.add(`item:${itemId}`);
  });
  // Also save which "read items" sections are expanded
  const expandedReadSections = new Set<string>();
  listEl.querySelectorAll(".read-items-container:not(.hidden)").forEach((el) => {
    const section = el.closest(".read-items-section");
    const sectionType = section?.getAttribute("data-section-type");
    if (sectionType) expandedReadSections.add(sectionType);
  });

  // Helper to check if item is relevant to current stage
  const isRelevantToStage = (appliesTo?: WritingStage[]): boolean => {
    if (!state.writingStage) return true;
    if (!appliesTo || appliesTo.length === 0) return true;
    return appliesTo.includes(state.writingStage);
  };

  // Build audience profile for conditional guidance matching
  const audienceProfile: AudienceProfile = {
    closeness: state.audience.closeness,
    agreement: state.audience.agreement,
    neighbors: state.audience.neighbors,
    faith: state.audience.faith,
    writingStage: state.writingStage,
  };

  // Get list cards for ordering and display metadata
  const topicsList = getListByType(cards, "topics");
  const blockersList = getListByType(cards, "blockers");

  // Collect topics - may be grouped or flat
  let topicGroups: GuidanceGroup[] | null = null;
  let topics: GuidanceItem[] = [];

  if (topicsList) {
    if (topicsList.groups) {
      // Grouped topics
      topicGroups = topicsList.groups
        .map(group => ({
          title: group.title,
          items: group.items
            .map(item => listItemToTopicGuidance(item, cards, audienceProfile, isRelevantToStage))
            .filter((item): item is GuidanceItem => item !== null),
        }))
        .filter(group => group.items.length > 0 || debugMode);
    } else if (topicsList.items) {
      // Flat topics
      topics = topicsList.items
        .map(item => listItemToTopicGuidance(item, cards, audienceProfile, isRelevantToStage))
        .filter((item): item is GuidanceItem => item !== null);
    }
  }

  // Collect blockers with strategies
  const blockers: BlockerItem[] = [];
  if (blockersList?.items) {
    for (const item of blockersList.items) {
      const blockerItem = listItemToBlockerItem(item, cards, isRelevantToStage, audienceProfile);
      if (blockerItem) blockers.push(blockerItem);
    }
  }

  // Collect techniques
  const techniquesList = getListByType(cards, "techniques");
  const techniques: TechniqueItem[] = [];
  if (techniquesList?.items) {
    for (const item of techniquesList.items) {
      const techniqueItem = listItemToTechniqueItem(item, cards, audienceProfile);
      if (techniqueItem) techniques.push(techniqueItem);
    }
  }

  const hasTopics = topicGroups ? topicGroups.length > 0 : topics.length > 0;
  const hasItems = hasTopics || blockers.length > 0 || techniques.length > 0;

  if (!hasItems) {
    listEl.classList.add("hidden");
    emptyEl?.classList.remove("hidden");
    return;
  }

  emptyEl?.classList.add("hidden");
  listEl.classList.remove("hidden");

  // Helper to count items and finished items
  const countItems = (items: { id: string }[]): { total: number; finished: number; unfinished: number } => {
    const total = items.length;
    const finished = items.filter(item => isRead(`guidance_${item.id}`)).length;
    return { total, finished, unfinished: total - finished };
  };

  const countGroupedItems = (groups: GuidanceGroup[]): { total: number; finished: number; unfinished: number } => {
    let total = 0, finished = 0;
    for (const group of groups) {
      total += group.items.length;
      finished += group.items.filter(item => isRead(`guidance_${item.id}`)).length;
    }
    return { total, finished, unfinished: total - finished };
  };

  // Format count display
  const formatCount = (counts: { total: number; finished: number; unfinished: number }, labelKey: "ideas" | "strategies" | "techniques"): string => {
    const label = tr()[labelKey];
    if (counts.finished === 0) {
      return `${counts.total} ${label}`;
    }
    return `${counts.unfinished}/${counts.total} ${label}`;
  };

  let html = "";

  // Topics section
  if (hasTopics && topicsList) {
    const topicCounts = topicGroups ? countGroupedItems(topicGroups) : countItems(topics);
    const topicsDescription = selectDescription(topicsList.description, topicsList.conditionalDescriptions, audienceProfile);
    html += `
      <details id="topics" class="group border-2 border-[#52C9E8] rounded-lg bg-white" data-section="topics">
        <summary class="cursor-pointer list-none px-4 py-3 hover:bg-gray-50 rounded-lg">
          <div class="flex items-start gap-4">
            <img src="/icon-topic.png" alt="" class="w-12 h-12 flex-shrink-0" />
            <div class="flex-1 min-w-0">
              <h2 class="text-lg font-semibold text-gray-900">${topicsList.header}</h2>
              <p class="text-sm text-gray-600 mt-1">${topicsDescription}</p>
            </div>
            <div class="text-right ml-4 flex-shrink-0">
              <span class="text-gray-400 group-open:rotate-180 transition-transform text-sm inline-block">▼</span>
              <p class="text-xs text-gray-400 mt-1">${formatCount(topicCounts, "ideas" as const)}</p>
            </div>
          </div>
        </summary>
        <div class="p-4 border-t border-[#52C9E8]">
          ${topicGroups ? renderGroupedAccordionItems(topicGroups, "topics") : renderAccordionItems(topics, "topics")}
        </div>
      </details>
    `;
  }

  // Blockers section
  if (blockers.length > 0 && blockersList) {
    const blockerCounts = countItems(blockers);
    const blockersDescription = selectDescription(blockersList.description, blockersList.conditionalDescriptions, audienceProfile);
    html += `
      <details id="blockers" class="group border-2 border-amber-500 rounded-lg mt-4 bg-white" data-section="blockers">
        <summary class="cursor-pointer list-none px-4 py-3 hover:bg-gray-50 rounded-lg">
          <div class="flex items-start gap-4">
            <img src="/icon-blocker.png" alt="" class="w-12 h-12 flex-shrink-0" />
            <div class="flex-1 min-w-0">
              <h2 class="text-lg font-semibold text-gray-900">${blockersList.header}</h2>
              <p class="text-sm text-gray-600 mt-1">${blockersDescription}</p>
            </div>
            <div class="text-right ml-4 flex-shrink-0">
              <span class="text-gray-400 group-open:rotate-180 transition-transform text-sm inline-block">▼</span>
              <p class="text-xs text-gray-400 mt-1">${formatCount(blockerCounts, "strategies" as const)}</p>
            </div>
          </div>
        </summary>
        <div class="p-4 border-t border-amber-500">
          ${renderBlockerItems(blockers, "blockers")}
        </div>
      </details>
    `;
  }

  // Techniques section - only show for "stuck" and "draft-review" stages
  const showTechniques = !state.writingStage || state.writingStage === "stuck" || state.writingStage === "draft-review";
  if (techniques.length > 0 && techniquesList && showTechniques) {
    const techniqueCounts = countItems(techniques);
    const techniquesDescription = selectDescription(techniquesList.description, techniquesList.conditionalDescriptions, audienceProfile);
    html += `
      <details id="techniques" class="group border-2 border-purple-400 rounded-lg mt-4 bg-white" data-section="techniques">
        <summary class="cursor-pointer list-none px-4 py-3 hover:bg-gray-50 rounded-lg">
          <div class="flex items-start gap-4">
            <img src="/icon-technique.png" alt="" class="w-12 h-12 flex-shrink-0" />
            <div class="flex-1 min-w-0">
              <h2 class="text-lg font-semibold text-gray-900">${techniquesList.header}</h2>
              <p class="text-sm text-gray-600 mt-1">${techniquesDescription}</p>
            </div>
            <div class="text-right ml-4 flex-shrink-0">
              <span class="text-gray-400 group-open:rotate-180 transition-transform text-sm inline-block">▼</span>
              <p class="text-xs text-gray-400 mt-1">${formatCount(techniqueCounts, "techniques" as const)}</p>
            </div>
          </div>
        </summary>
        <div class="p-4 border-t border-purple-400">
          ${renderTechniqueItems(techniques, "techniques")}
        </div>
      </details>
    `;
  }

  listEl.innerHTML = html;

  // Get hash from URL (e.g., #topics)
  const hash = window.location.hash.slice(1); // Remove the #

  // Restore open state for sections and items
  // Also auto-open when URL hash matches a section
  listEl.querySelectorAll("details[data-section]").forEach((el) => {
    const section = el.getAttribute("data-section");
    if (section && openSections.has(`section:${section}`)) {
      (el as HTMLDetailsElement).open = true;
    }
    // Auto-expand when URL hash matches this section
    if (section && hash === section) {
      (el as HTMLDetailsElement).open = true;
    }
    // #prompts opens the topics section
    if (section === "topics" && hash === "prompts") {
      (el as HTMLDetailsElement).open = true;
    }
  });
  listEl.querySelectorAll("details[data-item-id]").forEach((el) => {
    const itemId = el.getAttribute("data-item-id");
    if (itemId && openSections.has(`item:${itemId}`)) {
      (el as HTMLDetailsElement).open = true;
    }
    // #prompts expands all topic items that have prompts
    if (hash === "prompts" && el.hasAttribute("data-has-prompts")) {
      (el as HTMLDetailsElement).open = true;
    }
  });
  // Restore expanded read items sections
  listEl.querySelectorAll(".read-items-section").forEach((section) => {
    const sectionType = section.getAttribute("data-section-type");
    if (sectionType && expandedReadSections.has(sectionType)) {
      const container = section.querySelector(".read-items-container");
      const btn = section.querySelector(".show-read-btn");
      if (container) container.classList.remove("hidden");
      if (btn) btn.textContent = tr().hideReadItems;
    }
  });

  // Set up finished item toggle and button handlers
  setupFinishedItemHandlers(listEl);

  // Add copy buttons to any code blocks
  addCopyButtons(listEl);
}

function renderResearchSources(sources: ResearchSource[] | undefined): string {
  if (!showResearchMode || !sources || sources.length === 0) return '';

  const sourcesHtml = sources.map(source => {
    // Parse the ref to get a readable title and anchor ID
    // Format: "research/filename.card#item-id" -> title "Filename: Item Id", anchor "research_filename_item-id"
    const match = source.ref.match(/^research\/([^.]+)\.card(?:#(.+))?$/);
    let title = source.ref;
    let anchorId = '';
    if (match) {
      const filename = match[1];
      const itemId = match[2];
      const filenameTitle = filename.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      const itemIdTitle = itemId?.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      title = itemIdTitle ? `${filenameTitle}: ${itemIdTitle}` : filenameTitle;
      // Build anchor: research_filename_item-id
      anchorId = itemId ? `research_${filename}_${itemId}` : `research_${filename}`;
    }

    const linkHref = anchorId ? `/research.html#${anchorId}` : '/research.html';

    return `
      <div class="text-sm">
        <a href="${linkHref}" class="font-medium text-purple-800 hover:text-purple-900 hover:underline">${title}</a>
        <p class="text-purple-700 mt-1 ml-4 whitespace-pre-line">${source.summary}</p>
      </div>
    `;
  }).join('');

  return `
    <div class="mt-4 bg-purple-50 border border-purple-200 rounded-lg p-4">
      <p class="text-xs font-semibold text-purple-600 uppercase tracking-wide mb-3">${tr().researchSources}</p>
      <div class="space-y-3">
        ${sourcesHtml}
      </div>
    </div>
  `;
}

function renderPromptGroups(promptGroups: RenderedPromptsGroup[] | undefined): string {
  if (!promptGroups || promptGroups.length === 0) return '';

  return promptGroups.map(group => {
    // Debug mode classes for the group
    const groupHiddenClass = group.hidden ? "debug-hidden" : "";
    const groupMatchedClass = group.matched ? "debug-matched" : "";

    const titleHtml = group.title
      ? `<p class="text-sm font-bold text-gray-900 mt-4 mb-1">
          ${group.title}
          ${debugMode && group.matchedCondition ? `<span class="text-xs text-green-600 bg-green-50 px-1.5 py-0.5 rounded ml-2">${group.matchedCondition}</span>` : ""}
          ${debugMode && group.hiddenReason ? `<span class="text-xs text-red-600 bg-red-50 px-1.5 py-0.5 rounded ml-2">hidden: ${group.hiddenReason}</span>` : ""}
        </p>`
      : '';

    // In debug mode, render individual prompts with their match info
    let promptsHtml: string;
    if (debugMode && group.debugPrompts) {
      promptsHtml = group.debugPrompts.map(p => {
        const promptHiddenClass = p.hidden ? "debug-hidden" : "";
        const promptMatchedClass = p.matched ? "debug-matched" : "";
        const matchBadge = p.matchedCondition
          ? `<span class="text-xs text-green-600 bg-green-50 px-1 py-0.5 rounded ml-1">${p.matchedCondition}</span>`
          : "";
        const hiddenBadge = p.hiddenReason
          ? `<span class="text-xs text-red-600 bg-red-50 px-1 py-0.5 rounded ml-1">hidden: ${p.hiddenReason}</span>`
          : "";
        return `<li class="${promptHiddenClass} ${promptMatchedClass}">${p.text}${matchBadge}${hiddenBadge}</li>`;
      }).join('');
    } else {
      promptsHtml = group.prompts.map(p => `<li>${p}</li>`).join('');
    }

    return `
      <div class="${groupHiddenClass} ${groupMatchedClass}">
        ${titleHtml}
        <div class="ml-4 pl-4 border-l-2 border-l-lime-500 ${group.title ? '' : 'mt-4'}">
          <ul class="space-y-2 text-sm text-lime-800">
            ${promptsHtml}
          </ul>
        </div>
      </div>
    `;
  }).join('');
}

function renderAccordionItems(items: GuidanceItem[], sectionType: string): string {
  const unfinishedItems = items.filter(item => !isRead(`guidance_${item.id}`));
  const finishedItems = items.filter(item => isRead(`guidance_${item.id}`));

  // Track whether we've assigned id="prompts" to the first item with prompts
  let assignedPromptsId = false;

  let html = '<div class="space-y-2">';

  // Render unfinished items
  for (const item of unfinishedItems) {
    const hasPrompts = item.promptGroups && item.promptGroups.length > 0;
    const isFirstPrompts = hasPrompts && !assignedPromptsId && sectionType === "topics";
    if (isFirstPrompts) assignedPromptsId = true;
    html += renderSingleAccordionItem(item, sectionType, false, isFirstPrompts);
  }

  // Render read items section if any
  if (finishedItems.length > 0) {
    html += `
      <div class="read-items-section mt-4" data-section-type="${sectionType}">
        <button class="show-read-btn text-sm text-blue-600 hover:text-blue-800 hover:underline">
          ${tr().readItems} (${finishedItems.length})
        </button>
        <div class="read-items-container hidden mt-2 space-y-2">
          ${finishedItems.map(item => renderSingleAccordionItem(item, sectionType, true, false)).join('')}
        </div>
      </div>
    `;
  }

  html += '</div>';
  return html;
}

function renderSingleAccordionItem(item: GuidanceItem, sectionType: string, isFinished: boolean, isFirstPrompts: boolean = false): string {
  const promptsHtml = renderPromptGroups(item.promptGroups);
  const hasPrompts = item.promptGroups && item.promptGroups.length > 0;

  // Debug mode classes
  const hiddenClass = item.hidden ? "debug-hidden" : "";
  const matchedClass = item.matched ? "debug-matched" : "";
  const finishedClass = isFinished ? "opacity-60" : "";
  const debugAttr = debugMode && (item.hiddenReason || item.matchedCondition)
    ? `data-debug-info="${item.hiddenReason || item.matchedCondition}"`
    : "";
  const promptsAttr = hasPrompts ? 'data-has-prompts="true"' : "";
  // Add id="prompts" to the first topic item with prompts for hash navigation
  const promptsIdAttr = isFirstPrompts ? 'id="prompts"' : "";

  // Render guidance content - in debug mode, show all variants
  let guidanceHtml: string;
  if (debugMode && item.debugGuidanceVariants && item.debugGuidanceVariants.length > 1) {
    guidanceHtml = item.debugGuidanceVariants.map(variant => {
      const variantContent = marked.parse(variant.content) as string;
      const label = variant.match || "default";
      if (variant.isActive) {
        return `
          <div class="debug-guidance-active">
            <div class="text-xs text-green-600 bg-green-50 px-2 py-1 rounded mb-2 inline-block">${label}</div>
            <div class="prose prose-gray prose-sm max-w-none">${variantContent}</div>
          </div>
        `;
      } else {
        return `
          <div class="debug-guidance-inactive opacity-40 mt-4 pt-4 border-t border-dashed border-gray-300">
            <div class="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded mb-2 inline-block">${label}</div>
            <div class="prose prose-gray prose-sm max-w-none">${variantContent}</div>
          </div>
        `;
      }
    }).join('');
  } else {
    const contentHtml = marked.parse(item.content) as string;
    guidanceHtml = `<div class="prose prose-gray prose-sm max-w-none">${contentHtml}</div>`;
  }

  return `
    <details ${promptsIdAttr} class="bg-white rounded-lg border border-gray-200 group/item ${hiddenClass} ${matchedClass} ${finishedClass}" ${debugAttr} ${promptsAttr} data-item-id="${item.id}" data-section-type="${sectionType}">
      <summary class="px-4 py-3 cursor-pointer flex items-center gap-2 hover:bg-gray-50">
        ${isStarred(`guidance_${item.id}`) ? `<span class="text-yellow-500 star-indicator">★</span>` : ""}
        <span class="font-medium text-gray-900">${item.title}</span>
        ${isFinished ? `<span class="text-xs text-gray-400">${tr().readBadge}</span>` : ""}
        ${debugMode && item.matchedCondition ? `<span class="text-xs text-green-600 bg-green-50 px-1.5 py-0.5 rounded">${item.matchedCondition}</span>` : ""}
        ${debugMode && item.hiddenReason ? `<span class="text-xs text-red-600 bg-red-50 px-1.5 py-0.5 rounded">hidden: ${item.hiddenReason}</span>` : ""}
        <span class="ml-auto text-gray-400 group-open/item:rotate-180 transition-transform">▼</span>
      </summary>
      <div class="px-4 pb-4 border-t border-gray-100 pt-3">
        ${guidanceHtml}
        ${promptsHtml}
        ${renderResearchSources(item.sources)}
        <div class="mt-4 pt-3 border-t border-gray-100 flex justify-between items-center">
          <button class="btn-star text-sm px-3 py-1.5 rounded-lg border ${isStarred(`guidance_${item.id}`) ? "border-yellow-400 bg-yellow-50 text-yellow-700" : "border-gray-300 text-gray-500 hover:bg-gray-50"}">
            ${isStarred(`guidance_${item.id}`) ? tr().starred : tr().star}
          </button>
          <button class="btn-finished text-sm px-3 py-1.5 rounded-lg border ${isFinished ? "border-gray-300 text-gray-600 hover:bg-gray-50" : "border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100"}">
            ${isFinished ? tr().markAsUnread : tr().markAsDone}
          </button>
        </div>
      </div>
    </details>
  `;
}

function renderGroupedAccordionItems(groups: GuidanceGroup[], sectionType: string): string {
  let html = '<div class="space-y-4">';
  for (const group of groups) {
    // Count unfinished items in this group
    const unfinishedCount = group.items.filter(item => !isRead(`guidance_${item.id}`)).length;
    const totalCount = group.items.length;
    const countDisplay = unfinishedCount < totalCount
      ? `<span class="text-xs text-gray-400 ml-2">(${unfinishedCount}/${totalCount})</span>`
      : "";

    html += `
      <div class="space-y-2">
        <h3 class="text-sm font-medium text-gray-500 uppercase tracking-wide text-center">${group.title}${countDisplay}</h3>
        ${renderAccordionItems(group.items, sectionType)}
      </div>
    `;
  }
  html += '</div>';
  return html;
}

function renderBlockerItems(blockers: BlockerItem[], sectionType: string): string {
  const unfinishedBlockers = blockers.filter(b => !isRead(`guidance_${b.id}`));
  const finishedBlockers = blockers.filter(b => isRead(`guidance_${b.id}`));

  let html = '<div class="space-y-2">';

  // Render unfinished items
  for (const blocker of unfinishedBlockers) {
    html += renderSingleBlockerItem(blocker, sectionType, false);
  }

  // Render read items section if any
  if (finishedBlockers.length > 0) {
    html += `
      <div class="read-items-section mt-4" data-section-type="${sectionType}">
        <button class="show-read-btn text-sm text-blue-600 hover:text-blue-800 hover:underline">
          ${tr().readItems} (${finishedBlockers.length})
        </button>
        <div class="read-items-container hidden mt-2 space-y-2">
          ${finishedBlockers.map(b => renderSingleBlockerItem(b, sectionType, true)).join('')}
        </div>
      </div>
    `;
  }

  html += '</div>';
  return html;
}

function renderSingleBlockerItem(blocker: BlockerItem, sectionType: string, isFinished: boolean): string {
  const contentHtml = marked.parse(blocker.content) as string;

  // Debug mode classes for blocker
  const blockerHiddenClass = blocker.hidden ? "debug-hidden" : "";
  const finishedClass = isFinished ? "opacity-60" : "";

  // Check star status: blocker itself or any strategy starred
  const blockerStarred = isStarred(`guidance_${blocker.id}`);
  const anyStrategyStarred = blocker.strategies.some(s => isStarred(`guidance_${s.id}`));
  // Full star if blocker is starred, half star if only strategies are starred
  const starIndicator = blockerStarred
    ? `<span class="text-yellow-500 star-indicator">★</span>`
    : anyStrategyStarred
      ? `<span class="text-yellow-400 star-indicator">☆</span>`
      : "";

  const strategiesHtml = blocker.strategies.length > 0
    ? `<div class="mt-4 pt-4 border-t border-gray-200">
        <p class="text-sm font-medium text-gray-500 mb-2">${tr().strategiesToTry}</p>
        <div class="space-y-2">
          ${blocker.strategies.map(s => {
            const strategyContentHtml = marked.parse(s.content) as string;
            const strategyPromptsHtml = renderPromptGroups(s.promptGroups);
            // Debug mode classes for strategy
            const strategyHiddenClass = s.hidden ? "debug-hidden" : "";
            const hiddenBadge = debugMode && s.hiddenReason
              ? `<span class="text-xs text-red-600 bg-red-50 px-1.5 py-0.5 rounded ml-2">hidden: ${s.hiddenReason}</span>`
              : "";
            const strategyResearchHtml = renderResearchSources(s.sources);
            const strategyStarred = isStarred(`guidance_${s.id}`);
            return `
              <details class="bg-gray-50 rounded-lg border border-gray-200 group/strategy ${strategyHiddenClass}" data-item-id="${s.id}" data-section-type="strategies">
                <summary class="px-3 py-2 cursor-pointer flex items-center gap-2 hover:bg-gray-100 text-sm">
                  ${strategyStarred ? `<span class="text-yellow-500 star-indicator">★</span>` : ""}
                  <span class="font-medium text-gray-700">${s.label}</span>
                  ${hiddenBadge}
                  <span class="ml-auto text-gray-400 group-open/strategy:rotate-180 transition-transform text-xs">▼</span>
                </summary>
                <div class="px-3 pb-3 border-t border-gray-200">
                  <div class="prose prose-gray prose-sm max-w-none pt-2">${strategyContentHtml}</div>
                  ${strategyPromptsHtml}
                  ${strategyResearchHtml}
                  <div class="mt-3 pt-2 border-t border-gray-200 flex justify-start">
                    <button class="btn-star text-xs px-2 py-1 rounded border ${isStarred(`guidance_${s.id}`) ? "border-yellow-400 bg-yellow-50 text-yellow-700" : "border-gray-300 text-gray-500 hover:bg-gray-50"}">
                      ${isStarred(`guidance_${s.id}`) ? tr().starred : tr().star}
                    </button>
                  </div>
                </div>
              </details>
            `;
          }).join('')}
        </div>
      </div>`
    : '';

  const blockerHiddenBadge = debugMode && blocker.hiddenReason
    ? `<span class="text-xs text-red-600 bg-red-50 px-1.5 py-0.5 rounded">hidden: ${blocker.hiddenReason}</span>`
    : "";

  return `
    <details class="bg-white rounded-lg border border-gray-200 group/item ${blockerHiddenClass} ${finishedClass}" data-item-id="${blocker.id}" data-section-type="${sectionType}">
      <summary class="px-4 py-3 cursor-pointer flex items-center gap-2 hover:bg-gray-50">
        ${starIndicator}
        <span class="font-medium text-gray-900">${blocker.title}</span>
        ${isFinished ? `<span class="text-xs text-gray-400">${tr().readBadge}</span>` : ""}
        ${blockerHiddenBadge}
        <span class="ml-auto text-gray-400 group-open/item:rotate-180 transition-transform">▼</span>
      </summary>
      <div class="px-4 pb-4 border-t border-gray-100">
        <div class="prose prose-gray prose-sm max-w-none pt-3">${contentHtml}</div>
        ${strategiesHtml}
        ${renderResearchSources(blocker.sources)}
        <div class="mt-4 pt-3 border-t border-gray-100 flex justify-between items-center">
          <button class="btn-star text-sm px-3 py-1.5 rounded-lg border ${isStarred(`guidance_${blocker.id}`) ? "border-yellow-400 bg-yellow-50 text-yellow-700" : "border-gray-300 text-gray-500 hover:bg-gray-50"}">
            ${isStarred(`guidance_${blocker.id}`) ? tr().starred : tr().star}
          </button>
          <button class="btn-finished text-sm px-3 py-1.5 rounded-lg border ${isFinished ? "border-gray-300 text-gray-600 hover:bg-gray-50" : "border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100"}">
            ${isFinished ? tr().markAsUnread : tr().markAsDone}
          </button>
        </div>
      </div>
    </details>
  `;
}

function renderTechniqueItems(techniques: TechniqueItem[], sectionType: string): string {
  const unfinishedTechniques = techniques.filter(t => !isRead(`guidance_${t.id}`));
  const finishedTechniques = techniques.filter(t => isRead(`guidance_${t.id}`));

  let html = '<div class="space-y-2">';

  // Render unfinished items
  for (const technique of unfinishedTechniques) {
    html += renderSingleTechniqueItem(technique, sectionType, false);
  }

  // Render read items section if any
  if (finishedTechniques.length > 0) {
    html += `
      <div class="read-items-section mt-4" data-section-type="${sectionType}">
        <button class="show-read-btn text-sm text-blue-600 hover:text-blue-800 hover:underline">
          ${tr().readItems} (${finishedTechniques.length})
        </button>
        <div class="read-items-container hidden mt-2 space-y-2">
          ${finishedTechniques.map(t => renderSingleTechniqueItem(t, sectionType, true)).join('')}
        </div>
      </div>
    `;
  }

  html += '</div>';
  return html;
}

function renderSingleTechniqueItem(technique: TechniqueItem, sectionType: string, isFinished: boolean): string {
  const promptsHtml = renderPromptGroups(technique.promptGroups);

  // Debug mode classes
  const hiddenClass = technique.hidden ? "debug-hidden" : "";
  const matchedClass = technique.matched ? "debug-matched" : "";
  const finishedClass = isFinished ? "opacity-60" : "";

  // Render guidance content - in debug mode, show all variants
  let guidanceHtml: string;
  if (debugMode && technique.debugGuidanceVariants && technique.debugGuidanceVariants.length > 1) {
    guidanceHtml = technique.debugGuidanceVariants.map(variant => {
      const variantContent = marked.parse(variant.content) as string;
      const label = variant.match || "default";
      if (variant.isActive) {
        return `
          <div class="debug-guidance-active">
            <div class="text-xs text-green-600 bg-green-50 px-2 py-1 rounded mb-2 inline-block">${label}</div>
            <div class="prose prose-gray prose-sm max-w-none">${variantContent}</div>
          </div>
        `;
      } else {
        return `
          <div class="debug-guidance-inactive opacity-40 mt-4 pt-4 border-t border-dashed border-gray-300">
            <div class="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded mb-2 inline-block">${label}</div>
            <div class="prose prose-gray prose-sm max-w-none">${variantContent}</div>
          </div>
        `;
      }
    }).join('');
  } else {
    const contentHtml = marked.parse(technique.content) as string;
    guidanceHtml = `<div class="prose prose-gray prose-sm max-w-none">${contentHtml}</div>`;
  }

  // Render examples if present
  let examplesHtml = '';
  if (technique.examples && technique.examples.length > 0) {
    examplesHtml = `
      <div class="mt-4 pt-4 border-t border-gray-100">
        <p class="text-sm font-medium text-gray-700 mb-2">${tr().examplesLabel}</p>
        <div class="space-y-2">
          ${technique.examples.map(ex => `
            <div class="bg-gray-50 rounded-lg px-3 py-2 text-sm">
              ${ex.label ? `<span class="text-gray-500">${ex.label}</span> ` : ''}
              <span class="text-gray-800">${ex.text}</span>
            </div>
          `).join('')}
        </div>
      </div>
    `;
  }

  return `
    <details class="bg-white rounded-lg border border-gray-200 group/item ${hiddenClass} ${matchedClass} ${finishedClass}" data-item-id="${technique.id}" data-section-type="${sectionType}">
      <summary class="px-4 py-3 cursor-pointer flex items-center gap-2 hover:bg-gray-50">
        ${isStarred(`guidance_${technique.id}`) ? `<span class="text-yellow-500 star-indicator">★</span>` : ""}
        <span class="font-medium text-gray-900">${technique.title}</span>
        ${isFinished ? `<span class="text-xs text-gray-400">${tr().readBadge}</span>` : ""}
        ${debugMode && technique.matchedCondition ? `<span class="text-xs text-green-600 bg-green-50 px-1.5 py-0.5 rounded">${technique.matchedCondition}</span>` : ""}
        ${debugMode && technique.hiddenReason ? `<span class="text-xs text-red-600 bg-red-50 px-1.5 py-0.5 rounded">hidden: ${technique.hiddenReason}</span>` : ""}
        <span class="ml-auto text-gray-400 group-open/item:rotate-180 transition-transform">▼</span>
      </summary>
      <div class="px-4 pb-4 border-t border-gray-100 pt-3">
        ${guidanceHtml}
        ${examplesHtml}
        ${promptsHtml}
        ${renderResearchSources(technique.sources)}
        <div class="mt-4 pt-3 border-t border-gray-100 flex justify-between items-center">
          <button class="btn-star text-sm px-3 py-1.5 rounded-lg border ${isStarred(`guidance_${technique.id}`) ? "border-yellow-400 bg-yellow-50 text-yellow-700" : "border-gray-300 text-gray-500 hover:bg-gray-50"}">
            ${isStarred(`guidance_${technique.id}`) ? tr().starred : tr().star}
          </button>
          <button class="btn-finished text-sm px-3 py-1.5 rounded-lg border ${isFinished ? "border-gray-300 text-gray-600 hover:bg-gray-50" : "border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100"}">
            ${isFinished ? tr().markAsUnread : tr().markAsDone}
          </button>
        </div>
      </div>
    </details>
  `;
}

function getTopicTitle(id: string, listTitle?: string): string {
  const main = listTitle || formatId(id);
  return `<span class="text-gray-500 font-normal">${tr().writingAbout}</span> ${main}`;
}

// Stage color definitions: warm progression tomato red → orange → yellow
const stageColors: Record<string, { bgActive: string }> = {
  "not-started": { bgActive: "bg-[#e64a19]" },  // Tomato red
  "stuck": { bgActive: "bg-orange-500" },
  "draft-review": { bgActive: "bg-yellow-500" },
};

function applyStageStyles(btn: HTMLButtonElement, isActive: boolean): void {
  const stage = btn.dataset.stage || "";
  const colors = stageColors[stage];
  if (!colors) return;

  // Remove all stage-specific classes first
  Object.values(stageColors).forEach((c) => {
    btn.classList.remove(c.bgActive);
  });
  btn.classList.remove("text-white", "text-gray-600", "bg-white", "shadow-sm");

  if (isActive) {
    // Active: saturated colored background, white text
    btn.classList.add(colors.bgActive, "text-white");
  } else {
    // Inactive: no background, gray text
    btn.classList.add("text-gray-600");
  }
}

function initWritingStageNav(): void {
  const nav = document.getElementById("writing-stage-nav");
  if (!nav) return;

  // Set initial active state
  const buttons = nav.querySelectorAll<HTMLButtonElement>("button[data-stage]");
  buttons.forEach((btn) => {
    const isActive = btn.dataset.stage === state.writingStage;
    applyStageStyles(btn, isActive);
  });

  // Listen for clicks
  nav.addEventListener("click", (e) => {
    const btn = (e.target as HTMLElement).closest("button[data-stage]") as HTMLButtonElement | null;
    if (!btn) return;

    const newStage = btn.dataset.stage as WritingStage;
    state.writingStage = newStage;
    updateUrl(state);

    // Update active state visually
    buttons.forEach((b) => {
      const isActive = b.dataset.stage === newStage;
      applyStageStyles(b, isActive);
    });
  });
}

// Update a blocker's star indicator based on its own star state and strategies' star states
function updateBlockerStarIndicator(blockerEl: HTMLElement): void {
  const blockerId = blockerEl.dataset.itemId;
  if (!blockerId) return;

  const blockerStarred = isStarred(`guidance_${blockerId}`);
  const strategyEls = blockerEl.querySelectorAll("[data-section-type='strategies']");
  const anyStrategyStarred = Array.from(strategyEls).some(el => {
    const stratId = (el as HTMLElement).dataset.itemId;
    return stratId && isStarred(`guidance_${stratId}`);
  });

  const summary = blockerEl.querySelector(":scope > summary");
  if (!summary) return;

  const existingStar = summary.querySelector(".star-indicator");

  if (blockerStarred) {
    // Full star
    if (existingStar) {
      existingStar.textContent = "★";
      existingStar.className = "text-yellow-500 star-indicator";
    } else {
      const star = document.createElement("span");
      star.className = "text-yellow-500 star-indicator";
      star.textContent = "★";
      summary.insertBefore(star, summary.firstChild);
    }
  } else if (anyStrategyStarred) {
    // Half star (outline)
    if (existingStar) {
      existingStar.textContent = "☆";
      existingStar.className = "text-yellow-400 star-indicator";
    } else {
      const star = document.createElement("span");
      star.className = "text-yellow-400 star-indicator";
      star.textContent = "☆";
      summary.insertBefore(star, summary.firstChild);
    }
  } else {
    // No star
    if (existingStar) {
      existingStar.remove();
    }
  }
}

function setupFinishedItemHandlers(container: HTMLElement): void {
  // Handle "Mark as done" / "Mark as unread" and star buttons
  container.addEventListener("click", (e) => {
    const target = e.target as HTMLElement;

    // Handle star button
    if (target.classList.contains("btn-star")) {
      const itemEl = target.closest("[data-item-id]") as HTMLElement;
      if (itemEl) {
        const itemId = itemEl.dataset.itemId;
        if (itemId) {
          const starKey = `guidance_${itemId}`;
          const nowStarred = toggleStarred(starKey);
          // Update button appearance
          if (nowStarred) {
            target.classList.remove("border-gray-300", "text-gray-500", "hover:bg-gray-50");
            target.classList.add("border-yellow-400", "bg-yellow-50", "text-yellow-700");
            target.textContent = tr().starred;
          } else {
            target.classList.add("border-gray-300", "text-gray-500", "hover:bg-gray-50");
            target.classList.remove("border-yellow-400", "bg-yellow-50", "text-yellow-700");
            target.textContent = tr().star;
          }
          // Update star indicator in summary
          const summary = itemEl.querySelector("summary");
          if (summary) {
            const existingStar = summary.querySelector(".star-indicator");
            if (nowStarred && !existingStar) {
              const star = document.createElement("span");
              star.className = "text-yellow-500 star-indicator";
              star.textContent = "★";
              summary.insertBefore(star, summary.firstChild);
            } else if (!nowStarred && existingStar) {
              existingStar.remove();
            }
          }
          // Update parent blocker's half-star indicator if this is a strategy
          const sectionType = itemEl.dataset.sectionType;
          if (sectionType === "strategies") {
            const parentBlocker = itemEl.closest("[data-section-type='blockers']") as HTMLElement;
            if (parentBlocker) {
              updateBlockerStarIndicator(parentBlocker);
            }
          }
        }
      }
      return;
    }

    // Handle finished button
    if (target.classList.contains("btn-finished")) {
      const itemEl = target.closest("[data-item-id]") as HTMLElement;
      if (itemEl) {
        const itemId = itemEl.dataset.itemId;
        if (itemId) {
          const readKey = `guidance_${itemId}`;
          const currentlyFinished = isRead(readKey);

          if (currentlyFinished) {
            unmarkAsRead(readKey);
            // Re-render immediately
            initGuidePage();
          } else {
            markAsRead(readKey);
            // Show animation before re-rendering
            showMovedToReadAnimation(itemEl, () => {
              initGuidePage();
            });
          }
        }
      }
      return;
    }

    // Handle "Show read items" toggle
    const readBtn = target.closest(".show-read-btn") as HTMLElement;
    if (readBtn) {
      const section = readBtn.closest(".read-items-section") as HTMLElement;
      if (section) {
        const readContainer = section.querySelector(".read-items-container") as HTMLElement;
        if (readContainer) {
          const isHidden = readContainer.classList.contains("hidden");
          readContainer.classList.toggle("hidden");
          readBtn.textContent = isHidden
            ? tr().hideReadItems
            : `${tr().readItems} (${readContainer.querySelectorAll("[data-item-id]").length})`;
        }
      }
    }
  });
}

function showMovedToReadAnimation(itemEl: HTMLElement, callback: () => void): void {
  // Create the toast message
  const toast = document.createElement("div");
  toast.className = "fixed bottom-4 left-1/2 -translate-x-1/2 bg-gray-800 text-white px-4 py-2 rounded-lg text-sm shadow-lg z-50 transition-opacity duration-300";
  toast.textContent = tr().movedToReadItems;
  document.body.appendChild(toast);

  // Fade out the item
  itemEl.style.transition = "opacity 0.3s, transform 0.3s";
  itemEl.style.opacity = "0.3";
  itemEl.style.transform = "translateX(20px)";

  // After animation, remove toast and call callback
  setTimeout(() => {
    toast.style.opacity = "0";
    setTimeout(() => {
      toast.remove();
      callback();
    }, 300);
  }, 800);
}


function formatId(id: string): string {
  return id
    .replace(/^(topics_|blockers_|audiences_)/, "")
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

interface ListItem {
  ref: string;
  title: string;
}

interface ListGroup {
  title: string;
  items: ListItem[];
}

interface ListCard {
  listType: string;
  header: string;
  description: string;
  items?: ListItem[];
  groups?: ListGroup[];
}

interface ConditionalGuidance {
  match: string;
  content: string;
}

interface Prompt {
  match?: string;
  text: string;
}

interface PromptsGroup {
  title?: string;
  match?: string;
  prompts: Prompt[];
}

interface TechniqueExample {
  label?: string;
  text: string;
}

interface AllCards {
  topics: Record<string, { guidance: string; conditionalGuidance?: ConditionalGuidance[]; promptGroups?: PromptsGroup[]; match?: string; appliesTo?: WritingStage[]; sources?: ResearchSource[] }>;
  audiences: Record<string, { guidance: string; selectionText: string; sources?: ResearchSource[] }>;
  blockers: Record<string, { reassurance: string; strategies?: { ref: string; label: string }[]; appliesTo?: WritingStage[]; sources?: ResearchSource[] }>;
  strategies: Record<string, { guidance: string; appliesTo?: WritingStage[]; promptGroups?: PromptsGroup[]; sources?: ResearchSource[] }>;
  techniques: Record<string, { guidance: string; conditionalGuidance?: ConditionalGuidance[]; examples?: TechniqueExample[]; promptGroups?: PromptsGroup[]; match?: string; sources?: ResearchSource[] }>;
  lists: Record<string, ListCard>;
}

// Audience matching for conditional guidance
interface AudienceProfile {
  closeness?: "close" | "extended";
  agreement?: "sympathetic" | "some-disagree";
  neighbors?: boolean;
  faith?: boolean;
  writingStage?: "not-started" | "stuck" | "draft-review";
}

/**
 * Check if a match expression matches the current profile.
 * Match expressions are space-separated terms (AND logic).
 * Terms can be:
 *   Audience: close, extended, sympathetic, some-disagree, neighbors, faith
 *   Writing stage: not-started, stuck, draft-review
 * Prefix with ! for NOT (e.g., "!faith" means faith is not selected)
 *
 * Returns the number of terms matched (for specificity), or -1 if no match.
 */
function matchAudience(match: string, profile: AudienceProfile): number {
  const terms = match.split(/\s+/).filter(Boolean);
  let matchCount = 0;

  for (const term of terms) {
    const isNegated = term.startsWith("!");
    const key = isNegated ? term.slice(1) : term;

    let matches = false;

    switch (key) {
      // Audience terms
      case "close":
        matches = profile.closeness === "close";
        break;
      case "extended":
        matches = profile.closeness === "extended";
        break;
      case "sympathetic":
        matches = profile.agreement === "sympathetic";
        break;
      case "some-disagree":
        matches = profile.agreement === "some-disagree";
        break;
      case "neighbors":
        matches = profile.neighbors === true;
        break;
      case "faith":
        matches = profile.faith === true;
        break;
      // Writing stage terms
      case "not-started":
        matches = profile.writingStage === "not-started";
        break;
      case "stuck":
        matches = profile.writingStage === "stuck";
        break;
      case "draft-review":
        matches = profile.writingStage === "draft-review";
        break;
      // Special terms
      case "hide":
        matches = false; // Always false - hides the item unconditionally
        break;
      default:
        console.warn(`Unknown match term: ${key}`);
        return -1;
    }

    // Apply negation
    if (isNegated) matches = !matches;

    if (!matches) {
      return -1; // All terms must match (AND logic)
    }
    matchCount++;
  }

  return matchCount;
}

/**
 * Select the best matching guidance for a topic based on audience profile.
 * Returns the most specific match, or default guidance if no conditional matches.
 */
function selectGuidance(
  defaultGuidance: string,
  conditionalGuidance: ConditionalGuidance[] | undefined,
  profile: AudienceProfile
): string {
  return selectGuidanceWithDebug(defaultGuidance, conditionalGuidance, profile).content;
}

/**
 * Select the best matching description for a list section based on state.
 */
function selectDescription(
  defaultDescription: string,
  conditionalDescriptions: { match: string; content: string }[] | undefined,
  profile: AudienceProfile
): string {
  if (!conditionalDescriptions || conditionalDescriptions.length === 0) {
    return defaultDescription;
  }

  let bestMatch: { match: string; content: string } | null = null;
  let bestSpecificity = 0;

  for (const cd of conditionalDescriptions) {
    const specificity = matchAudience(cd.match, profile);
    if (specificity > bestSpecificity) {
      bestSpecificity = specificity;
      bestMatch = cd;
    }
  }

  return bestMatch ? bestMatch.content : defaultDescription;
}

interface GuidanceResult {
  content: string;
  matched: boolean; // true if a conditional match was used (not default)
  matchedCondition?: string; // the match expression that was used
  debugVariants?: DebugGuidanceVariant[]; // all variants for debug display
}

function selectGuidanceWithDebug(
  defaultGuidance: string,
  conditionalGuidance: ConditionalGuidance[] | undefined,
  profile: AudienceProfile
): GuidanceResult {
  if (!conditionalGuidance || conditionalGuidance.length === 0) {
    return { content: defaultGuidance, matched: false };
  }

  let bestMatch: ConditionalGuidance | null = null;
  let bestSpecificity = 0;

  for (const cg of conditionalGuidance) {
    const specificity = matchAudience(cg.match, profile);
    if (specificity > bestSpecificity) {
      bestSpecificity = specificity;
      bestMatch = cg;
    }
  }

  // Build debug variants if in debug mode
  let debugVariants: DebugGuidanceVariant[] | undefined;
  if (debugMode) {
    debugVariants = [
      // Default guidance
      {
        content: defaultGuidance,
        match: undefined,
        isActive: !bestMatch,
      },
      // All conditional variants
      ...conditionalGuidance.map(cg => ({
        content: cg.content,
        match: cg.match,
        isActive: cg === bestMatch,
      })),
    ];
  }

  if (bestMatch) {
    return {
      content: bestMatch.content,
      matched: true,
      matchedCondition: bestMatch.match,
      debugVariants,
    };
  }
  return { content: defaultGuidance, matched: false, debugVariants };
}

/**
 * Select prompt groups that match the current audience profile.
 * Groups without a match condition are always included.
 * Individual prompts within groups can also have match conditions.
 * In debug mode, returns hidden items with metadata.
 */
function selectPromptGroups(
  promptGroups: PromptsGroup[] | undefined,
  profile: AudienceProfile
): RenderedPromptsGroup[] {
  if (!promptGroups || promptGroups.length === 0) {
    return [];
  }

  const result: RenderedPromptsGroup[] = [];

  for (const g of promptGroups) {
    const groupMatches = !g.match || matchAudience(g.match, profile) >= 0;
    const groupHasCondition = !!g.match;

    if (!groupMatches && !debugMode) {
      continue; // Skip hidden groups in non-debug mode
    }

    const renderedPrompts: string[] = [];
    const debugPrompts: RenderedPrompt[] = [];

    for (const p of g.prompts) {
      const promptMatches = !p.match || matchAudience(p.match, profile) >= 0;
      const promptHasCondition = !!p.match;

      if (debugMode) {
        debugPrompts.push({
          text: p.text,
          matched: promptHasCondition && promptMatches,
          matchedCondition: promptHasCondition && promptMatches ? p.match : undefined,
          hidden: !promptMatches,
          hiddenReason: !promptMatches ? p.match : undefined,
        });
      }

      if (promptMatches) {
        renderedPrompts.push(p.text);
      }
    }

    // In debug mode, include groups even if empty (all prompts hidden)
    if (renderedPrompts.length > 0 || debugMode) {
      result.push({
        title: g.title,
        prompts: renderedPrompts,
        debugPrompts: debugMode ? debugPrompts : undefined,
        matched: groupHasCondition && groupMatches,
        matchedCondition: groupHasCondition && groupMatches ? g.match : undefined,
        hidden: !groupMatches,
        hiddenReason: !groupMatches ? g.match : undefined,
      });
    }
  }

  return result;
}

function initGuidancePage(): void {
  const contentEl = document.getElementById("guidance-content");
  const noSelectionsEl = document.getElementById("no-selections");
  const introEl = document.getElementById("guide-intro");
  const cardsDataEl = document.getElementById("cards-data");

  if (!contentEl || !cardsDataEl) return;

  // Parse cards data
  const cards: AllCards = JSON.parse(cardsDataEl.textContent || "{}");

  // Check if we have any selections at all
  const hasAnySelections = state.writingStage ||
    state.topics.length > 0 ||
    state.audience.closeness ||
    state.audience.agreement ||
    state.audience.neighbors ||
    state.audience.faith ||
    state.blockers.length > 0;

  if (!hasAnySelections) {
    contentEl.classList.add("hidden");
    noSelectionsEl?.classList.remove("hidden");
    return;
  }

  noSelectionsEl?.classList.add("hidden");
  contentEl.classList.remove("hidden");

  if (introEl) {
    if (state.writingStage) {
      introEl.textContent = `${tr().youSaid} "${tr().stages[state.writingStage]}" ${tr().heresGuidance}`;
    } else {
      introEl.textContent = tr().basedOnSelections;
    }
  }

  // Helper to check if item is relevant to current stage
  const isRelevantToStage = (appliesTo?: WritingStage[]): boolean => {
    if (!state.writingStage) return true; // No stage selected, everything is relevant
    if (!appliesTo || appliesTo.length === 0) return true; // No restriction, always relevant
    return appliesTo.includes(state.writingStage);
  };

  // Build audience profile for conditional guidance matching
  const audienceProfile: AudienceProfile = {
    closeness: state.audience.closeness,
    agreement: state.audience.agreement,
    neighbors: state.audience.neighbors,
    faith: state.audience.faith,
    writingStage: state.writingStage,
  };

  // Build the guidance content
  let html = "";

  // Topics section
  if (state.topics.length > 0) {
    html += '<section class="space-y-6">';
    html += '<h2 class="text-2xl font-bold">What to Write About</h2>';

    for (const topicId of state.topics) {
      const topic = cards.topics[topicId];
      if (topic) {
        const title = formatId(topicId);
        const guidance = selectGuidance(topic.guidance, topic.conditionalGuidance, audienceProfile);
        const guidanceHtml = marked.parse(guidance) as string;
        const itemIsRead = isRead(`topic_${topicId}`);
        const readClass = itemIsRead ? "opacity-60" : "";
        const readBadge = itemIsRead ? '<span class="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded ml-2">reviewed</span>' : "";

        html += `
          <div class="bg-white rounded-lg border border-gray-200 p-6 ${readClass}" data-item-id="topic_${topicId}">
            <h3 class="text-lg font-semibold mb-4">${title}${readBadge}</h3>
            <div class="prose prose-gray max-w-none">${guidanceHtml}</div>
            <div class="flex gap-3 mt-4 pt-4 border-t border-gray-100">
              <button class="btn-nevermind text-sm text-gray-500 hover:text-gray-700">Nevermind</button>
              <button class="btn-got-it text-sm bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700">Ok, got it</button>
            </div>
          </div>
        `;
      }
    }
    html += "</section>";
  }

  // Audience section
  const hasAudience = state.audience.closeness || state.audience.agreement || state.audience.neighbors || state.audience.faith;
  if (hasAudience) {
    html += '<section class="space-y-6 mt-8">';
    html += '<h2 class="text-2xl font-bold">Writing for Your Audience</h2>';

    // Combine relevant audience guidance
    const audienceIds = [
      state.audience.closeness ? `audiences_${state.audience.closeness}` : null,
      state.audience.agreement ? `audiences_${state.audience.agreement}` : null,
      state.audience.neighbors ? `audiences_neighbors` : null,
      state.audience.faith ? `audiences_faith-community` : null,
    ].filter(Boolean) as string[];

    for (const audienceId of audienceIds) {
      const audience = cards.audiences[audienceId];
      if (audience) {
        const guidanceHtml = marked.parse(audience.guidance) as string;
        const itemIsRead = isRead(`audience_${audienceId}`);
        const readClass = itemIsRead ? "opacity-60" : "";
        const readBadge = itemIsRead ? '<span class="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded ml-2">reviewed</span>' : "";

        html += `
          <div class="bg-white rounded-lg border border-gray-200 p-6 ${readClass}" data-item-id="audience_${audienceId}">
            <h3 class="text-lg font-semibold mb-4">${audience.selectionText}${readBadge}</h3>
            <div class="prose prose-gray max-w-none">${guidanceHtml}</div>
            <div class="flex gap-3 mt-4 pt-4 border-t border-gray-100">
              <button class="btn-nevermind text-sm text-gray-500 hover:text-gray-700">Nevermind</button>
              <button class="btn-got-it text-sm bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700">Ok, got it</button>
            </div>
          </div>
        `;
      }
    }
    html += "</section>";
  }

  // Blockers section - split into relevant and other based on writing stage
  if (state.blockers.length > 0) {
    const relevantBlockers = state.blockers.filter(id => {
      const blocker = cards.blockers[id];
      return blocker && isRelevantToStage(blocker.appliesTo);
    });
    const otherBlockers = state.blockers.filter(id => {
      const blocker = cards.blockers[id];
      return blocker && !isRelevantToStage(blocker.appliesTo);
    });

    if (relevantBlockers.length > 0) {
      html += '<section class="space-y-6 mt-8">';
      html += '<h2 class="text-2xl font-bold">Getting Past What\'s Holding You Back</h2>';
      html += renderBlockersList(relevantBlockers, cards, false);
      html += "</section>";
    }

    if (otherBlockers.length > 0) {
      html += '<section class="space-y-6 mt-8">';
      html += '<h2 class="text-xl font-semibold text-gray-500">Other guidance you selected</h2>';
      html += renderBlockersList(otherBlockers, cards, true);
      html += "</section>";
    }
  }

  contentEl.innerHTML = html;

  // Set up event listeners for buttons
  setupGuidanceButtons(contentEl);
}

function renderBlockersList(blockerIds: string[], cards: AllCards, dimmed: boolean): string {
  let html = '<div class="space-y-4">';

  for (const blockerId of blockerIds) {
    const blocker = cards.blockers[blockerId];
    if (blocker) {
      const title = formatId(blockerId);
      const reassuranceHtml = marked.parse(blocker.reassurance) as string;
      const itemIsRead = isRead(`blocker_${blockerId}`);
      const readClass = (itemIsRead || dimmed) ? "opacity-60" : "";
      const readBadge = itemIsRead ? '<span class="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded ml-2">reviewed</span>' : "";

      html += `
        <div class="bg-white rounded-lg border border-gray-200 p-6 ${readClass}" data-item-id="blocker_${blockerId}">
          <h3 class="text-lg font-semibold mb-4">${title}${readBadge}</h3>
          <div class="prose prose-gray max-w-none mb-4">${reassuranceHtml}</div>
      `;

      // Add strategies if available
      if (blocker.strategies && blocker.strategies.length > 0) {
        html += '<div class="border-t border-gray-100 pt-4 mt-4">';
        html += `<h4 class="font-medium text-gray-700 mb-3">${tr().strategiesToTry}</h4>`;
        html += '<div class="space-y-4">';

        for (const stratRef of blocker.strategies) {
          const stratId = stratRef.ref
            .replace("blockers/strategies/", "blockers_strategies_")
            .replace(".card", "");
          const strategy = cards.strategies[stratId];
          if (strategy) {
            const strategyHtml = marked.parse(strategy.guidance) as string;
            const stratIsRead = isRead(`strategy_${stratId}`);
            const stratReadClass = stratIsRead ? "bg-gray-100" : "bg-gray-50";
            const stratReadBadge = stratIsRead ? ' <span class="text-xs text-gray-400">(reviewed)</span>' : "";

            html += `
              <details class="${stratReadClass} rounded-lg p-4" data-item-id="strategy_${stratId}">
                <summary class="font-medium cursor-pointer">${stratRef.label}${stratReadBadge}</summary>
                <div class="prose prose-gray prose-sm max-w-none mt-3">${strategyHtml}</div>
                <div class="flex gap-3 mt-4 pt-3 border-t border-gray-200">
                  <button class="btn-nevermind text-sm text-gray-500 hover:text-gray-700">Nevermind</button>
                  <button class="btn-got-it text-sm bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700">Ok, got it</button>
                </div>
              </details>
            `;
          }
        }
        html += "</div></div>";
      }

      html += `
        <div class="flex gap-3 mt-4 pt-4 border-t border-gray-100">
          <button class="btn-nevermind text-sm text-gray-500 hover:text-gray-700">Nevermind</button>
          <button class="btn-got-it text-sm bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700">Ok, got it</button>
        </div>
      </div>`;
    }
  }

  html += "</div>";
  return html;
}

function setupGuidanceButtons(container: HTMLElement): void {
  // Handle "Ok, got it" buttons
  container.addEventListener("click", (e) => {
    const target = e.target as HTMLElement;

    if (target.classList.contains("btn-got-it")) {
      const itemEl = target.closest("[data-item-id]") as HTMLElement;
      if (itemEl) {
        const itemId = itemEl.dataset.itemId;
        if (itemId) {
          markAsRead(itemId);
          // Update the visual state
          itemEl.classList.add("opacity-60");
          // Add "reviewed" badge if not already present
          const heading = itemEl.querySelector("h3, summary");
          if (heading && !heading.querySelector(".bg-gray-100")) {
            heading.insertAdjacentHTML("beforeend", '<span class="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded ml-2">reviewed</span>');
          }
        }
        // If it's a details element, close it
        const details = itemEl.closest("details");
        if (details) {
          details.open = false;
        }
      }
    }

    if (target.classList.contains("btn-nevermind")) {
      // Just close if it's a details element
      const details = target.closest("details");
      if (details) {
        details.open = false;
      }
      // For top-level items, we could scroll to next or do nothing
    }
  });
}

// Print handling - expand all details before printing, restore after
let detailsStateBeforePrint: Map<HTMLDetailsElement, boolean> = new Map();

function handleBeforePrint(): void {
  detailsStateBeforePrint.clear();

  // Save current state and open all details
  document.querySelectorAll("details").forEach((details) => {
    detailsStateBeforePrint.set(details, details.open);
    details.open = true;
  });

  // Show all read items sections
  document.querySelectorAll(".read-items-container").forEach((container) => {
    container.classList.remove("hidden");
  });
}

function handleAfterPrint(): void {
  // Restore previous state
  detailsStateBeforePrint.forEach((wasOpen, details) => {
    details.open = wasOpen;
  });
  detailsStateBeforePrint.clear();

  // Restore read items sections to hidden (they'll re-render correctly on next interaction)
  document.querySelectorAll(".read-items-container").forEach((container) => {
    // Check if the section was expanded before print by looking at the button text
    // Look for "Hide" keyword which appears in both "Hide read items" / "Ocultar elementos leídos"
    const section = container.closest(".read-items-section");
    const btn = section?.querySelector(".show-read-btn");
    if (btn && !btn.textContent?.includes(tr().hideReadItems.split(" ")[0])) {
      container.classList.add("hidden");
    }
  });
}

window.addEventListener("beforeprint", handleBeforePrint);
window.addEventListener("afterprint", handleAfterPrint);

// Run on DOM ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
